import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Pagination } from '@nextui-org/react';

interface PaginationWrapperProps {
  currentPage: number;
  totalItems: number; // Total count from server
  itemsPerPage?: number;
  onPageChange: (handlePageChange: any) => void;
  children?: React.ReactNode; // Optional children
}

const PaginationWrapper = ({
  currentPage,
  totalItems,
  itemsPerPage = 10,
  onPageChange,
  children,
}: PaginationWrapperProps) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  const handleNext = () =>
    currentPage < totalPages && onPageChange(currentPage + 1);
  const handlePrev = () => currentPage > 1 && onPageChange(currentPage - 1);

  return (
    <div className="mt-6 space-y-6">
      {/* Render children directly (your table content) */}
      {children}

      <div className="flex w-full justify-between items-center max-sm:flex-col gap-2">
        <div className="text-sm text-gray-700">
          {t("showing")} {startIndex} {t("to")} {endIndex} {t("of")}{" "}
          {totalItems} {t("Results")}
        </div>

        <div className="flex flex-col justify-between gap-3">
          <div className="flex max-sm:flex-col sm:items-center gap-2 items-center">
            <Button
              color="primary"
              size="sm"
              className="max-sm:hidden"
              variant="flat"
              onPress={handlePrev}
              isDisabled={currentPage === 1}
            >
              {t("Previous")}
            </Button>

            <div className="flex justify-center">
              <Pagination
                color="secondary"
                page={currentPage}
                total={totalPages}
                onChange={onPageChange}
                classNames={{
                  item: "bg-success text-white data-[selected=true]:opacity-100",
                  cursor: "bg-success text-white",
                }}
              />
            </div>

            <Button
              color="primary"
              size="sm"
              className="max-sm:hidden"
              variant="flat"
              onPress={handleNext}
              isDisabled={currentPage === totalPages}
            >
              {t("Next")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaginationWrapper;